import { useEffect, useMemo, useRef, useState } from 'react';
import {
  usePayrollPolicies,
  useEffectivePayrollPolicy,
  useCreatePayrollPolicy,
  useUpdatePayrollPolicy,
  useDeletePayrollPolicy,
  useBulkDeletePayrollPolicies,
  useDepartments,
} from '../../services/httpServices/queries';
import { HrModal } from './modals/HrModal';
import { PayrollPolicyDetailsModal } from './modals/PayrollPolicyDetailsModal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Pagination } from '../../components/ui/Pagination';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { PageHelp } from '../../components/ui/PageHelp';
import { InfoHint, type BilingualHint } from '../../components/ui/InfoHint';
import type { DailyRateBasis, PayrollPolicy } from '../../services/httpServices/hrService';
import {
  SlidersHorizontal,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Star,
  Settings2,
  Clock,
  Percent,
  CalendarOff,
  ShieldCheck,
  Info,
  X,
  type LucideIcon,
} from 'lucide-react';

const inputClass =
  'w-full px-3 py-2.5 border border-border rounded-xl bg-background text-text placeholder:text-text-muted/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/25 text-sm transition-colors';
const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-text-muted';

const BASIS_LABEL: Record<DailyRateBasis, string> = {
  CALENDAR_DAYS: 'Calendar days',
  FIXED_DAYS: 'Fixed working days',
  WORKING_DAYS: 'Actual working days',
};

/**
 * Bilingual (English + Bangla) help shown from the info icon beside each
 * field label in the policy builder. Keeping it in one place makes the copy
 * easy to review and reuse.
 */
const FIELD_HELP = {
  name: {
    en: 'A clear name that identifies this policy, e.g. the department or staff group it covers.',
    bn: 'এই নীতিমালার একটি স্পষ্ট নাম দিন, যেমন যে বিভাগ বা কর্মী গোষ্ঠীর জন্য এটি প্রযোজ্য।',
  },
  department: {
    en: 'The department this policy applies to. Leave empty to use it as the global default when no department override exists.',
    bn: 'এই নীতিমালা যে বিভাগে প্রযোজ্য। খালি রাখলে কোনো বিভাগের নির্দিষ্ট নীতিমালা না থাকলে এটি বৈশ্বিক ডিফল্ট হিসেবে ব্যবহৃত হবে।',
  },
  isDefault: {
    en: 'Marks this as the fallback policy applied when a department has no override.',
    bn: 'এটি ফলব্যাক নীতিমালা হিসেবে চিহ্নিত করে, যা কোনো বিভাগের নির্দিষ্ট নীতিমালা না থাকলে প্রযোজ্য হয়।',
  },
  isActive: {
    en: 'Only active policies are applied to payroll calculations.',
    bn: 'শুধুমাত্র সক্রিয় নীতিমালা বেতন হিসাবের জন্য প্রয়োগ করা হয়।',
  },
  basis: {
    en: 'How the daily rate is derived: calendar days, fixed working days or actual working days.',
    bn: 'দৈনিক হার কীভাবে নির্ধারিত হবে: ক্যালেন্ডার দিন, নির্দিষ্ট কর্মদিবস বা প্রকৃত কর্মদিবস।',
  },
  fixedWorkingDays: {
    en: 'The number of working days used when the basis is set to fixed working days.',
    bn: 'বেসিস “নির্দিষ্ট কর্মদিবস” হলে যে সংখ্যক কর্মদিবস ব্যবহার করা হয়।',
  },
  standardHoursPerDay: {
    en: 'Standard working hours per day, used as the divisor to calculate the hourly rate.',
    bn: 'দৈনিক আদর্শ কর্মঘণ্টা, যা ঘণ্টার হার হিসাবের ভাজক হিসেবে ব্যবহৃত হয়।',
  },
  overtimeMultiplier: {
    en: 'Overtime pay on a normal working day, expressed as a multiple of the hourly rate (e.g. 2 = double).',
    bn: 'সাধারণ কর্মদিবসে ওভারটাইম বেতন, ঘণ্টার হারের গুণিতক হিসেবে (যেমন ২ = দ্বিগুণ)।',
  },
  holidayOvertimeMultiplier: {
    en: 'Overtime pay on a holiday or weekly off, expressed as a multiple of the hourly rate.',
    bn: 'ছুটির দিন বা সাপ্তাহিক বন্ধে ওভারটাইম বেতন, ঘণ্টার হারের গুণিতক হিসেবে।',
  },
  maxOvertimeHours: {
    en: 'Maximum overtime hours payable per pay period. 0 means unlimited.',
    bn: 'প্রতি বেতনকালে সর্বোচ্চ প্রদেয় ওভারটাইম ঘণ্টা। ০ মানে সীমাহীন।',
  },
  overtimeGraceMinutes: {
    en: 'Overtime shorter than this many minutes is ignored and not paid.',
    bn: 'এই মিনিটের কম ওভারটাইম উপেক্ষা করা হয় এবং পরিশোধ করা হয় না।',
  },
  overtimeRoundingMinutes: {
    en: 'Overtime is rounded down to this increment before it is paid.',
    bn: 'পরিশোধের আগে ওভারটাইম এই ইনক্রিমেন্টে নিচের দিকে রাউন্ড করা হয়।',
  },
  minimumOvertimeMinutes: {
    en: 'The smallest overtime duration that is paid. Shorter amounts are not paid.',
    bn: 'সর্বনিম্ন ওভারটাইম সময় যা পরিশোধ করা হয়। এর চেয়ে কম হলে পরিশোধ করা হয় না।',
  },
  maxOvertimeHoursPerDay: {
    en: 'Maximum overtime hours payable in a single day. 0 means unlimited.',
    bn: 'একদিনে সর্বোচ্চ প্রদেয় ওভারটাইম ঘণ্টা। ০ মানে সীমাহীন।',
  },
  maxOvertimeHoursPerWeek: {
    en: 'Maximum overtime hours payable in a week. 0 means unlimited.',
    bn: 'সপ্তাহে সর্বোচ্চ প্রদেয় ওভারটাইম ঘণ্টা। ০ মানে সীমাহীন।',
  },
  absentDeductionMultiplier: {
    en: "Loss-of-pay days charged for each absent day (e.g. 1 = one day's pay deducted).",
    bn: 'প্রতিটি অনুপস্থিত দিনের জন্য কাটা বেতনহীন দিন (যেমন ১ = একদিনের বেতন কাটা)।',
  },
  deductUnapprovedAbsence: {
    en: 'When enabled, any scheduled working day with no attendance and no approved leave is treated as an unapproved absence and one day of pay is deducted. Approve leave from HR → Leave to avoid the deduction.',
    bn: 'চালু থাকলে, কোনো নির্ধারিত কর্মদিবসে উপস্থিতি এবং অনুমোদিত ছুটি — দুটোই না থাকলে সেটি অননুমোদিত অনুপস্থিতি হিসেবে গণ্য হবে এবং একদিনের বেতন কাটা হবে। কাটা এড়াতে HR → Leave থেকে ছুটি অনুমোদন করুন।',
  },
  latePenaltyDays: {
    en: 'Loss-of-pay days charged for each late arrival.',
    bn: 'প্রতিবার দেরিতে আসার জন্য কাটা বেতনহীন দিন।',
  },
  minimumNetPay: {
    en: 'A floor for net pay that is protected from salary-advance recovery. 0 disables the floor.',
    bn: 'নেট বেতনের একটি ন্যূনতম সীমা যা বেতন অগ্রিম আদায় থেকে সুরক্ষিত থাকে। ০ দিলে সীমা নিষ্ক্রিয় হয়।',
  },
  prorateAllowances: {
    en: 'Scale allowances by payable days in the same way as basic salary, instead of paying them in full.',
    bn: 'ভাতাগুলো পূর্ণ পরিশোধ না করে মূল বেতনের মতোই প্রদেয় দিনের অনুপাতে হিসাব করুন।',
  },
  weeklyOffDays: {
    en: 'Fallback off days used only when an employee has no weekly off days of their own. Set each employee’s actual off days on their employee profile; employees can differ (Friday, Sunday, split weekends, etc.).',
    bn: 'শুধুমাত্র সেইসব কর্মীর জন্য ফলব্যাক বন্ধের দিন, যাদের নিজস্ব সাপ্তাহিক বন্ধের দিন নির্ধারণ করা নেই। প্রতিটি কর্মীর প্রকৃত বন্ধের দিন তার কর্মী প্রোফাইলে সেট করুন; কর্মীভেদে দিন ভিন্ন হতে পারে (শুক্রবার, রবিবার, ভিন্ন সমন্বয় ইত্যাদি)।',
  },
} satisfies Record<string, BilingualHint>;

const WEEKDAYS = [
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
  { value: 0, short: 'Sun' },
];

type PolicyTabId = 'general' | 'rates' | 'overtime' | 'deductions';

const POLICY_TABS: { id: PolicyTabId; label: string; icon: LucideIcon }[] = [
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'rates', label: 'Pay Rates', icon: Percent },
  { id: 'overtime', label: 'Overtime', icon: Clock },
  { id: 'deductions', label: 'Deductions', icon: ShieldCheck },
];

interface PolicyFormState {
  name: string;
  departmentId: string;
  isDefault: boolean;
  isActive: boolean;
  dailyRateBasis: DailyRateBasis;
  fixedWorkingDays: string;
  standardHoursPerDay: string;
  overtimeMultiplier: string;
  holidayOvertimeMultiplier: string;
  maxOvertimeHours: string;
  overtimeGraceMinutes: string;
  overtimeRoundingMinutes: string;
  minimumOvertimeMinutes: string;
  maxOvertimeHoursPerDay: string;
  maxOvertimeHoursPerWeek: string;
  prorateAllowances: boolean;
  latePenaltyDays: string;
  absentDeductionMultiplier: string;
  deductUnapprovedAbsence: boolean;
  minimumNetPay: string;
  weeklyOffDays: number[];
}

const emptyForm = (): PolicyFormState => ({
  name: '',
  departmentId: '',
  isDefault: false,
  isActive: true,
  dailyRateBasis: 'FIXED_DAYS',
  fixedWorkingDays: '26',
  standardHoursPerDay: '8',
  overtimeMultiplier: '2',
  holidayOvertimeMultiplier: '2',
  maxOvertimeHours: '0',
  overtimeGraceMinutes: '0',
  overtimeRoundingMinutes: '0',
  minimumOvertimeMinutes: '0',
  maxOvertimeHoursPerDay: '0',
  maxOvertimeHoursPerWeek: '0',
  prorateAllowances: false,
  latePenaltyDays: '0',
  absentDeductionMultiplier: '1',
  deductUnapprovedAbsence: true,
  minimumNetPay: '0',
  weeklyOffDays: [5],
});

function Field({
  label,
  hint,
  help,
  children,
}: {
  label: string;
  hint?: string;
  help?: BilingualHint;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <label className={labelClass}>{label}</label>
        {help && <InfoHint en={help.en} bn={help.bn} label={label} />}
      </div>
      {children}
      {hint && <p className="mt-1.5 text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

/** Panel that groups a set of related policy fields. */
function SectionCard({
  icon: Icon,
  title,
  description,
  help,
  children,
  className = '',
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  help?: BilingualHint;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-2xl border border-border bg-background/40 p-4 sm:p-5 space-y-4 ${className}`}>
      <header className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon size={16} className="text-primary" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-text">{title}</h3>
            {help && <InfoHint en={help.en} bn={help.bn} label={title} />}
          </div>
          {description && <p className="text-xs text-text-muted">{description}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

/** Modern switch row used for the boolean policy flags. */
function Switch({
  checked,
  onChange,
  label,
  hint,
  help,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint?: string;
  help?: BilingualHint;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
        checked ? 'border-primary/40 bg-primary/5' : 'border-border bg-surface hover:border-primary/30'
      }`}
    >
      <span
        className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          className={`h-4 w-4 rounded-full bg-[#fff] shadow-sm transition-transform ${
            checked ? 'translate-x-[1.15rem]' : 'translate-x-0.5'
          }`}
        />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-text">{label}</span>
          {help && <InfoHint en={help.en} bn={help.bn} label={label} />}
        </span>
        {hint && <span className="block text-xs text-text-muted">{hint}</span>}
      </span>
    </button>
  );
}

/** Read-only preview of the policy currently being configured. */
function PolicySummary({ form, departmentName }: { form: PolicyFormState; departmentName?: string }) {
  const rows: { label: string; value: string }[] = [
    { label: 'Scope', value: departmentName || (form.isDefault ? 'All departments' : 'Global default') },
    {
      label: 'Daily rate',
      value:
        form.dailyRateBasis === 'FIXED_DAYS'
          ? `${form.fixedWorkingDays || 0} fixed days`
          : BASIS_LABEL[form.dailyRateBasis],
    },
    {
      label: 'Overtime',
      value: `${form.overtimeMultiplier || 0}× normal · ${form.holidayOvertimeMultiplier || 0}× holiday`,
    },
    { label: 'Standard hours', value: `${form.standardHoursPerDay || 0} h / day` },
    { label: 'Absence', value: `${form.absentDeductionMultiplier || 0}× per day` },
    {
      label: 'Unapproved absence',
      value: form.deductUnapprovedAbsence ? 'Deducted' : 'Not deducted',
    },
  ];

  const offDays = WEEKDAYS.filter((day) => form.weeklyOffDays.includes(day.value)).map((day) => day.short);

  return (
    <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4 sm:p-5 space-y-4">
      <header className="flex items-center gap-2">
        <Info size={15} className="text-primary" />
        <h3 className="text-sm font-semibold text-text">Summary</h3>
        <div className="ml-auto flex items-center gap-1.5">
          {form.isDefault && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              <Star size={10} />
              Default
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
              form.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
            }`}
          >
            {form.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </header>

      <dl className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3 text-sm">
            <dt className="text-text-muted">{row.label}</dt>
            <dd className="text-right font-medium text-text">{row.value}</dd>
          </div>
        ))}
      </dl>

      <div className="border-t border-primary/15 pt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Default weekly off</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {offDays.length ? (
            offDays.map((day) => (
              <span key={day} className="rounded-md border border-border bg-surface px-2 py-0.5 text-xs font-medium text-text">
                {day}
              </span>
            ))
          ) : (
            <span className="text-xs text-text-muted">No weekly off days selected</span>
          )}
        </div>
      </div>
    </section>
  );
}

export function PayrollPolicyPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PayrollPolicy | null>(null);
  const [form, setForm] = useState<PolicyFormState>(emptyForm());
  const [formError, setFormError] = useState('');
  const [activeTab, setActiveTab] = useState<PolicyTabId>('general');
  const [deleteTarget, setDeleteTarget] = useState<PayrollPolicy | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<PayrollPolicy | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = usePayrollPolicies();
  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments();
  const createPolicy = useCreatePayrollPolicy();
  const updatePolicy = useUpdatePayrollPolicy();
  const deletePolicy = useDeletePayrollPolicy();
  const bulkDeletePolicy = useBulkDeletePayrollPolicies();

  const policies = useMemo(() => data?.data || [], [data]);

  const total = policies.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagePolicies = useMemo(
    () => policies.slice((page - 1) * pageSize, page * pageSize),
    [policies, page, pageSize],
  );

  // Keep the current page valid after deletes or a page-size change.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Drop selections for policies that no longer exist.
  useEffect(() => {
    const available = new Set(policies.map((policy) => policy.id));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => available.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [policies]);

  const allSelected = pagePolicies.length > 0 && pagePolicies.every((policy) => selectedIds.includes(policy.id));
  const someSelected = pagePolicies.some((policy) => selectedIds.includes(policy.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAllOnPage = () => {
    const pageIds = pagePolicies.map((policy) => policy.id);
    setSelectedIds((prev) =>
      allSelected ? prev.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds])),
    );
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const changePageSize = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const departmentOptions = useMemo(
    () =>
      (departmentsData?.data || []).map((department: any) => ({
        value: department.id,
        label: department.name,
        description: department.code,
      })),
    [departmentsData],
  );

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
    setFormOpen(false);
    setFormError('');
    setActiveTab('general');
  };

  const startCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setFormError('');
    setActiveTab('general');
    setFormOpen(true);
  };

  const startEdit = (policy: PayrollPolicy) => {
    setEditing(policy);
    setForm({
      name: policy.name,
      departmentId: policy.departmentId || '',
      isDefault: policy.isDefault,
      isActive: policy.isActive,
      dailyRateBasis: policy.dailyRateBasis,
      fixedWorkingDays: String(policy.fixedWorkingDays ?? 26),
      standardHoursPerDay: String(policy.standardHoursPerDay ?? 8),
      overtimeMultiplier: String(policy.overtimeMultiplier ?? 2),
      holidayOvertimeMultiplier: String(policy.holidayOvertimeMultiplier ?? 2),
      maxOvertimeHours: String(policy.maxOvertimeHours ?? 0),
      overtimeGraceMinutes: String(policy.overtimeGraceMinutes ?? 0),
      overtimeRoundingMinutes: String(policy.overtimeRoundingMinutes ?? 0),
      minimumOvertimeMinutes: String(policy.minimumOvertimeMinutes ?? 0),
      maxOvertimeHoursPerDay: String(policy.maxOvertimeHoursPerDay ?? 0),
      maxOvertimeHoursPerWeek: String(policy.maxOvertimeHoursPerWeek ?? 0),
      prorateAllowances: Boolean(policy.prorateAllowances),
      latePenaltyDays: String(policy.latePenaltyDays ?? 0),
      absentDeductionMultiplier: String(policy.absentDeductionMultiplier ?? 1),
      deductUnapprovedAbsence: policy.deductUnapprovedAbsence !== false,
      minimumNetPay: String(policy.minimumNetPay ?? 0),
      weeklyOffDays: Array.isArray(policy.weeklyOffDays) ? policy.weeklyOffDays : [5],
    });
    setFormError('');
    setActiveTab('general');
    setFormOpen(true);
  };

  const toggleWeekday = (day: number) => {
    setForm((prev) => ({
      ...prev,
      weeklyOffDays: prev.weeklyOffDays.includes(day)
        ? prev.weeklyOffDays.filter((d) => d !== day)
        : [...prev.weeklyOffDays, day].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim()) {
      setFormError('A policy name is required.');
      setActiveTab('general');
      return;
    }

    const toNumber = (value: string, fallback = 0) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const payload = {
      name: form.name.trim(),
      departmentId: form.departmentId || undefined,
      isDefault: form.isDefault,
      isActive: form.isActive,
      dailyRateBasis: form.dailyRateBasis,
      fixedWorkingDays: toNumber(form.fixedWorkingDays, 26),
      standardHoursPerDay: toNumber(form.standardHoursPerDay, 8),
      overtimeMultiplier: toNumber(form.overtimeMultiplier, 2),
      holidayOvertimeMultiplier: toNumber(form.holidayOvertimeMultiplier, 2),
      maxOvertimeHours: toNumber(form.maxOvertimeHours, 0),
      overtimeGraceMinutes: toNumber(form.overtimeGraceMinutes, 0),
      overtimeRoundingMinutes: toNumber(form.overtimeRoundingMinutes, 0),
      minimumOvertimeMinutes: toNumber(form.minimumOvertimeMinutes, 0),
      maxOvertimeHoursPerDay: toNumber(form.maxOvertimeHoursPerDay, 0),
      maxOvertimeHoursPerWeek: toNumber(form.maxOvertimeHoursPerWeek, 0),
      prorateAllowances: form.prorateAllowances,
      latePenaltyDays: toNumber(form.latePenaltyDays, 0),
      absentDeductionMultiplier: toNumber(form.absentDeductionMultiplier, 1),
      deductUnapprovedAbsence: form.deductUnapprovedAbsence,
      minimumNetPay: toNumber(form.minimumNetPay, 0),
      weeklyOffDays: form.weeklyOffDays,
    };

    const onError = (err: any) => {
      const message = err?.response?.data?.message || err?.message || 'Could not save the policy.';
      setFormError(Array.isArray(message) ? message.join(', ') : String(message));
    };

    if (editing) {
      updatePolicy.mutate({ id: editing.id, data: payload }, { onSuccess: resetForm, onError });
    } else {
      createPolicy.mutate(payload, { onSuccess: resetForm, onError });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePolicy.mutateAsync(deleteTarget.id);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await bulkDeletePolicy.mutateAsync(selectedIds);
      setSelectedIds([]);
      setBulkConfirmOpen(false);
    } catch {
      // Error toast is handled by the global MutationCache.
    }
  };

  const openDetails = (policy: PayrollPolicy) => setDetailsTarget(policy);

  const editFromDetails = (policy: PayrollPolicy) => {
    setDetailsTarget(null);
    startEdit(policy);
  };

  const submitting = createPolicy.isPending || updatePolicy.isPending;

  const activeTabIndex = POLICY_TABS.findIndex((tab) => tab.id === activeTab);
  const goToTab = (offset: number) => {
    const next = POLICY_TABS[activeTabIndex + offset];
    if (next) setActiveTab(next.id);
  };

  const selectedDepartmentName = departmentOptions.find((option) => option.value === form.departmentId)?.label;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <SlidersHorizontal className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text">Payroll Policy</h1>
              <PageHelp path="/hr/payroll-policy" />
            </div>
            <p className="text-sm text-text-muted">
              Rates, overtime and deduction rules used to calculate every pay run.
            </p>
          </div>
        </div>
        <button
          onClick={startCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          New Policy
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load payroll policies</span>
        </div>
      )}

      <EffectivePolicyCard departmentOptions={departmentOptions} departmentsLoading={departmentsLoading} />

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
          <div className="flex items-center gap-2 text-sm text-text">
            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-white">
              {selectedIds.length}
            </span>
            selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeletePolicy.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-danger px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-danger/90 disabled:opacity-50"
            >
              {bulkDeletePolicy.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete selected
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={bulkDeletePolicy.isPending}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-text-muted transition-colors hover:text-text disabled:opacity-50"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="w-10 px-4 py-3 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAllOnPage}
                      disabled={pagePolicies.length === 0}
                      className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary disabled:cursor-default"
                      title="Select all on this page"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Policy</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Applies to</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Daily rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Overtime</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {policies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                      No payroll policies configured. The built-in defaults are used until you add one.
                    </td>
                  </tr>
                ) : (
                  pagePolicies.map((policy) => (
                    <tr
                      key={policy.id}
                      className={`transition-colors ${
                        selectedIds.includes(policy.id) ? 'bg-primary/5' : 'hover:bg-background/50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(policy.id)}
                          onChange={() => toggleOne(policy.id)}
                          className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                          aria-label={`Select ${policy.name}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openDetails(policy)}
                            className="text-left text-sm font-medium text-text underline-offset-2 transition-colors hover:text-primary hover:underline"
                            title="View policy details"
                          >
                            {policy.name}
                          </button>
                          {policy.isDefault && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-primary/10 text-primary">
                              <Star size={10} />
                              Default
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {policy.department?.name || (policy.isDefault ? 'All departments' : '—')}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {BASIS_LABEL[policy.dailyRateBasis] ?? policy.dailyRateBasis}
                        {policy.dailyRateBasis === 'FIXED_DAYS' && (
                          <span className="block text-xs">{policy.fixedWorkingDays} days</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {Number(policy.overtimeMultiplier)}× normal · {Number(policy.holidayOvertimeMultiplier)}× holiday
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            policy.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {policy.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEdit(policy)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(policy)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {policies.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2">
                <label className="flex items-center gap-2 text-xs text-text-muted">
                  Rows per page
                  <select
                    value={pageSize}
                    onChange={(event) => changePageSize(Number(event.target.value))}
                    className="rounded-lg border border-border bg-background px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    {[10, 25, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={pageSize}
                onPageChange={setPage}
                itemName="policies"
              />
            </>
          )}
        </div>
      )}

      {formOpen && (
        <HrModal
          open
          onClose={resetForm}
          title={editing ? 'Edit Payroll Policy' : 'New Payroll Policy'}
          subtitle="Set the rates, overtime and deduction rules used by every pay run"
          icon={Settings2}
          maxWidth="max-w-5xl"
          tabs={POLICY_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
          footer={
            <>
              <span className="mr-auto hidden items-center gap-1.5 text-xs text-text-muted sm:flex">
                <Info size={13} />
                Changes apply to future payroll runs
              </span>
              {activeTabIndex > 0 && (
                <button
                  type="button"
                  onClick={() => goToTab(-1)}
                  className="hidden px-3 py-2 text-sm text-text-muted hover:text-text transition-colors sm:inline-flex"
                >
                  Back
                </button>
              )}
              {activeTabIndex < POLICY_TABS.length - 1 && (
                <button
                  type="button"
                  onClick={() => goToTab(1)}
                  className="hidden px-3 py-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors sm:inline-flex"
                >
                  Next
                </button>
              )}
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="policy-form"
                disabled={submitting}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {editing ? 'Save Changes' : 'Create Policy'}
              </button>
            </>
          }
        >
          <form id="policy-form" onSubmit={handleSubmit} className="space-y-5">
            {formError && (
              <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm font-medium text-danger">
                <AlertCircle size={16} className="shrink-0" />
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div className="space-y-5 lg:col-span-2">
                {activeTab === 'general' && (
                  <SectionCard
                    icon={Settings2}
                    title="Policy Identity"
                    description="Name the policy and choose the department scope it applies to."
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Policy Name" hint="Shown across payroll runs and reports." help={FIELD_HELP.name}>
                        <input
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="e.g. Nursing staff"
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Department" hint="Leave empty to use it as the global default." help={FIELD_HELP.department}>
                        <SearchableSelect
                          value={form.departmentId}
                          onChange={(departmentId) => setForm({ ...form, departmentId })}
                          options={departmentOptions}
                          placeholder="Global default"
                          loading={departmentsLoading}
                        />
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Switch
                        checked={form.isDefault}
                        onChange={(isDefault) => setForm({ ...form, isDefault })}
                        label="Default policy"
                        hint="Used when a department has no override."
                        help={FIELD_HELP.isDefault}
                      />
                      <Switch
                        checked={form.isActive}
                        onChange={(isActive) => setForm({ ...form, isActive })}
                        label="Active policy"
                        hint="Only active policies are applied to payroll."
                        help={FIELD_HELP.isActive}
                      />
                    </div>
                  </SectionCard>
                )}

                {activeTab === 'rates' && (
                  <SectionCard
                    icon={Percent}
                    title="Daily Rate"
                    description="How the daily and hourly rate is derived."
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <Field label="Basis" help={FIELD_HELP.basis}>
                        <select
                          value={form.dailyRateBasis}
                          onChange={(e) => setForm({ ...form, dailyRateBasis: e.target.value as DailyRateBasis })}
                          className={inputClass}
                        >
                          {(Object.keys(BASIS_LABEL) as DailyRateBasis[]).map((basis) => (
                            <option key={basis} value={basis}>
                              {BASIS_LABEL[basis]}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Fixed working days" hint="Used when basis is fixed." help={FIELD_HELP.fixedWorkingDays}>
                        <ValidatedNumberInput
                          decimals={0}
                          value={form.fixedWorkingDays}
                          onChange={(v) => setForm({ ...form, fixedWorkingDays: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Standard hours / day" hint="Divisor for the hourly rate." help={FIELD_HELP.standardHoursPerDay}>
                        <ValidatedNumberInput
                          value={form.standardHoursPerDay}
                          onChange={(v) => setForm({ ...form, standardHoursPerDay: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                    </div>
                    <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm text-text">
                      <span className="font-medium">Daily rate: </span>
                      20,000 ÷ {form.dailyRateBasis === 'FIXED_DAYS' ? form.fixedWorkingDays || 0 : 'working days'} =
                      {' '}
                      <span className="font-semibold text-primary">
                        ৳
                        {form.dailyRateBasis === 'FIXED_DAYS' && Number(form.fixedWorkingDays) > 0
                          ? (20000 / Number(form.fixedWorkingDays)).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })
                          : '—'}
                      </span>{' '}
                      per day. Each employee can override the divisor on their profile.
                    </div>
                  </SectionCard>
                )}

                {activeTab === 'overtime' && (
                  <SectionCard
                    icon={Clock}
                    title="Overtime"
                    description="Multipliers, caps and rounding applied to overtime pay."
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      <Field label="Normal-day multiplier" help={FIELD_HELP.overtimeMultiplier}>
                        <ValidatedNumberInput
                          value={form.overtimeMultiplier}
                          onChange={(v) => setForm({ ...form, overtimeMultiplier: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Holiday multiplier" help={FIELD_HELP.holidayOvertimeMultiplier}>
                        <ValidatedNumberInput
                          value={form.holidayOvertimeMultiplier}
                          onChange={(v) => setForm({ ...form, holidayOvertimeMultiplier: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Max OT hours / period" hint="0 = unlimited." help={FIELD_HELP.maxOvertimeHours}>
                        <ValidatedNumberInput
                          value={form.maxOvertimeHours}
                          onChange={(v) => setForm({ ...form, maxOvertimeHours: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Grace (minutes)" hint="Ignore OT shorter than this." help={FIELD_HELP.overtimeGraceMinutes}>
                        <ValidatedNumberInput
                          decimals={0}
                          value={form.overtimeGraceMinutes}
                          onChange={(v) => setForm({ ...form, overtimeGraceMinutes: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Rounding (minutes)" hint="Round OT down to this increment." help={FIELD_HELP.overtimeRoundingMinutes}>
                        <ValidatedNumberInput
                          decimals={0}
                          value={form.overtimeRoundingMinutes}
                          onChange={(v) => setForm({ ...form, overtimeRoundingMinutes: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Minimum payable (minutes)" hint="Do not pay below this." help={FIELD_HELP.minimumOvertimeMinutes}>
                        <ValidatedNumberInput
                          decimals={0}
                          value={form.minimumOvertimeMinutes}
                          onChange={(v) => setForm({ ...form, minimumOvertimeMinutes: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Max OT hours / day" hint="0 = unlimited." help={FIELD_HELP.maxOvertimeHoursPerDay}>
                        <ValidatedNumberInput
                          value={form.maxOvertimeHoursPerDay}
                          onChange={(v) => setForm({ ...form, maxOvertimeHoursPerDay: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Max OT hours / week" hint="0 = unlimited." help={FIELD_HELP.maxOvertimeHoursPerWeek}>
                        <ValidatedNumberInput
                          value={form.maxOvertimeHoursPerWeek}
                          onChange={(v) => setForm({ ...form, maxOvertimeHoursPerWeek: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                    </div>
                  </SectionCard>
                )}

                {activeTab === 'deductions' && (
                  <SectionCard
                    icon={ShieldCheck}
                    title="Deductions &amp; Leave"
                    description="Loss-of-pay rules and protected net pay."
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <Field label="Absence multiplier" hint="LOP days charged per absent day." help={FIELD_HELP.absentDeductionMultiplier}>
                        <ValidatedNumberInput
                          value={form.absentDeductionMultiplier}
                          onChange={(v) => setForm({ ...form, absentDeductionMultiplier: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Late penalty (days)" hint="LOP days per late occurrence." help={FIELD_HELP.latePenaltyDays}>
                        <ValidatedNumberInput
                          value={form.latePenaltyDays}
                          onChange={(v) => setForm({ ...form, latePenaltyDays: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Minimum net pay" hint="Protected from advance recovery. 0 = none." help={FIELD_HELP.minimumNetPay}>
                        <ValidatedNumberInput
                          value={form.minimumNetPay}
                          onChange={(v) => setForm({ ...form, minimumNetPay: String(v) })}
                          className={inputClass}
                        />
                      </Field>
                    </div>
                    <Switch
                      checked={form.deductUnapprovedAbsence}
                      onChange={(deductUnapprovedAbsence) =>
                        setForm({ ...form, deductUnapprovedAbsence })
                      }
                      label="Deduct unapproved absence"
                      hint="A working day with no attendance and no approved leave costs one day of pay."
                      help={FIELD_HELP.deductUnapprovedAbsence}
                    />
                    <Switch
                      checked={form.prorateAllowances}
                      onChange={(prorateAllowances) => setForm({ ...form, prorateAllowances })}
                      label="Prorate allowances"
                      hint="Scale allowances by payable days like the basic salary."
                      help={FIELD_HELP.prorateAllowances}
                    />
                  </SectionCard>
                )}
              </div>

              <div className="space-y-5">
                <PolicySummary form={form} departmentName={selectedDepartmentName} />

                {activeTab === 'general' && (
                  <SectionCard
                    icon={CalendarOff}
                    title="Default Weekly Off Days"
                    description="Fallback only — each employee can have different off days on their profile."
                    help={FIELD_HELP.weeklyOffDays}
                  >
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map((day) => {
                        const selected = form.weeklyOffDays.includes(day.value);
                        return (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => toggleWeekday(day.value)}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                              selected
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border text-text-muted hover:border-primary/30 hover:text-text'
                            }`}
                          >
                            {day.short}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-text-muted">
                      These are fallback days only. Open an employee and set <strong>Weekly Off Days</strong> on
                      their profile so each person can have a different weekend. Off days are never loss-of-pay
                      and count as holiday overtime when worked.
                    </p>
                  </SectionCard>
                )}
              </div>
            </div>
          </form>
        </HrModal>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete payroll policy?"
        description="Payroll will fall back to the default policy (or built-in defaults) for this scope."
        confirmLabel="Delete policy"
        loading={deletePolicy.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} payroll polic${selectedIds.length === 1 ? 'y' : 'ies'}?`}
        description="These policies will be removed and payroll will fall back to the remaining defaults for their scopes."
        confirmLabel={`Delete ${selectedIds.length} polic${selectedIds.length === 1 ? 'y' : 'ies'}`}
        loading={bulkDeletePolicy.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <PayrollPolicyDetailsModal
        policy={detailsTarget}
        onClose={() => setDetailsTarget(null)}
        onEdit={editFromDetails}
      />
    </div>
  );
}

function EffectivePolicyCard({
  departmentOptions,
  departmentsLoading,
}: {
  departmentOptions: { value: string; label: string; description?: string }[];
  departmentsLoading: boolean;
}) {
  const [departmentId, setDepartmentId] = useState('');
  const { data, isLoading } = useEffectivePayrollPolicy(departmentId || undefined);
  const policy = data?.data;

  const chips: { label: string; value: string }[] = policy
    ? [
        { label: 'Daily rate basis', value: BASIS_LABEL[policy.dailyRateBasis] ?? policy.dailyRateBasis },
        { label: 'Overtime', value: `${Number(policy.overtimeMultiplier)}× / ${Number(policy.holidayOvertimeMultiplier)}× holiday` },
        { label: 'Absence', value: `${Number(policy.absentDeductionMultiplier)}× per day` },
        {
          label: 'Unapproved absence',
          value: policy.deductUnapprovedAbsence !== false ? 'Deducted' : 'Not deducted',
        },
        { label: 'Standard hours', value: `${Number(policy.standardHoursPerDay)} h/day` },
      ]
    : [];

  return (
    <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Info size={16} className="text-primary" />
          <span className="text-sm font-medium text-text">Effective policy</span>
          <span className="text-xs text-text-muted">— what actually applies to a department</span>
        </div>
        <div className="w-full sm:w-64">
          <SearchableSelect
            value={departmentId}
            onChange={setDepartmentId}
            options={departmentOptions}
            placeholder="All departments (default)"
            loading={departmentsLoading}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <Loader2 size={14} className="animate-spin" />
          Resolving…
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <span
              key={chip.label}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-xs"
            >
              <span className="text-text-muted">{chip.label}:</span>
              <span className="font-medium text-text">{chip.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
